import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, BookOpen, Sparkles, X } from "lucide-react";
import { apiErrorText } from "@/lib/api";
import { Markdown } from "@/lib/markdown";
import { t } from "@/lib/i18n";
import { useAuth } from "./auth";
import { APP_BY_TYPE, APP_GROUPS } from "./app-catalog";
import { useAppNav } from "./app-nav";
import { useThemePortal } from "./channel-wizard-kit";
import { helpApi, type HelpAnswer, type HelpCard } from "./help-api";
import { cn } from "./ui";

/* Этап 13 — «Как пользоваться» на главной: вопрос помощнику по статьям (OpenRouter), статьи по группам разделов,
   статья открывается в окне поверх главной; из статьи можно открыть сам раздел. Тексты — docs/help/<lang>/*.md. */

const input =
  "h-10 w-full min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground outline-none transition placeholder:text-muted-foreground focus:border-primary";

export default function HelpBlock() {
  const { user } = useAuth();
  const { open } = useAppNav();
  const [items, setItems] = useState<HelpCard[]>([]);
  const [ai, setAi] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<HelpAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slug, setSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    helpApi.list().then((r) => {
      setItems(r.items);
      setAi(r.ai);
    }, () => setItems([]));
  }, [user]);

  const groups = useMemo(() => {
    const bySection = new Map<string, HelpCard[]>();
    for (const it of items) bySection.set(it.section, [...(bySection.get(it.section) ?? []), it]);
    const out: { title: string; items: HelpCard[] }[] = [];
    const seen = new Set<string>();
    for (const g of APP_GROUPS) {
      const list = g.items.flatMap((i) => bySection.get(i.type) ?? []);
      list.forEach((i) => seen.add(i.slug));
      if (list.length) out.push({ title: g.title, items: list });
    }
    const rest = items.filter((i) => !seen.has(i.slug));
    if (rest.length) out.unshift({ title: t("Общее"), items: rest });
    return out;
  }, [items]);

  const submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const q = question.trim();
      if (!q || busy) return;
      setBusy(true);
      setError(null);
      helpApi.ask(q).then(setAnswer, (err) => setError(apiErrorText(err))).finally(() => setBusy(false));
    },
    [question, busy],
  );

  if (!user || items.length === 0) return null;

  return (
    <section className="mt-6" id="help" data-testid="help-block">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold tracking-tight">
            <BookOpen className="size-4 text-primary" aria-hidden />
            {t("Как пользоваться")}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("Короткие статьи по каждому разделу и помощник, который отвечает по ним.")}</p>
        </div>
      </div>

      <form onSubmit={submit} className="rounded-btn border border-border bg-card p-3">
        <label htmlFor="help-question" className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
          <Sparkles className="size-3.5 text-primary" aria-hidden />
          {t("Спросить помощника")}
        </label>
        <div className="flex gap-2">
          <input
            id="help-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t("Например: как передать диалог коллеге?")}
            maxLength={1000}
            className={input}
            disabled={!ai}
          />
          <button
            type="submit"
            disabled={!ai || busy || !question.trim()}
            className="h-10 flex-none rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
          >
            {busy ? t("Думаю…") : t("Спросить")}
          </button>
        </div>
        {!ai && <p className="mt-1.5 text-[12px] text-muted-foreground">{t("Помощник не включён: нет ключа OpenRouter. Статьи ниже работают без него.")}</p>}
        {error && <p className="mt-1.5 text-[12px] text-red-600">{error}</p>}
        {answer && (
          <div className="mt-3 rounded-btn border border-border bg-background px-3 py-2.5" data-testid="help-answer">
            <Markdown text={answer.answer} />
            {answer.sources.length > 0 && (
              <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                <span>{t("Подробнее:")}</span>
                {answer.sources.map((s) => (
                  <button key={s.slug} type="button" onClick={() => setSlug(s.slug)} className="rounded-full border border-border bg-card px-2 py-0.5 text-foreground hover:border-primary/60">
                    {s.title}
                  </button>
                ))}
              </p>
            )}
          </div>
        )}
      </form>

      {groups.map((g) => (
        <div key={g.title} className="mt-3">
          <h3 className="mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-[.16em] text-muted-foreground">{g.title}</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {g.items.map((a) => (
              <button
                key={a.slug}
                type="button"
                onClick={() => setSlug(a.slug)}
                className="group flex min-w-0 cursor-pointer items-start gap-2.5 rounded-btn border border-border bg-card px-3 py-2.5 text-left transition hover:border-primary/60 hover:bg-background-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <span className="min-w-0 flex-1">
                  <b className="block text-[13px] font-semibold leading-tight text-foreground">{a.title}</b>
                  <span className="mt-0.5 block text-[11.5px] leading-snug text-muted-foreground">{a.summary}</span>
                </span>
                <ArrowRight className="mt-0.5 size-4 flex-none text-muted-foreground transition group-hover:text-primary" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      ))}

      {slug && <ArticleDialog slug={slug} onClose={() => setSlug(null)} onOpenSection={(type) => { setSlug(null); open(type); }} />}
    </section>
  );
}

function ArticleDialog({ slug, onClose, onOpenSection }: { slug: string; onClose: () => void; onOpenSection: (type: string) => void }) {
  const host = useThemePortal();
  const [state, setState] = useState<{ title: string; body: string; section: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setState(null);
    setError(null);
    helpApi.article(slug).then((a) => setState({ title: a.title, body: a.body, section: a.section }), (e) => setError(apiErrorText(e)));
  }, [slug]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const app = state ? APP_BY_TYPE[state.section] : undefined;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4 md:p-6" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-article-title"
        onClick={(e) => e.stopPropagation()}
        className="flash-modal relative flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-t-card border border-border sm:rounded-card"
      >
        <div className="flex items-start gap-3 border-b border-border px-4 py-3.5 md:px-5">
          <span className="grid size-10 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
            {app ? <app.icon className="size-5" /> : <BookOpen className="size-5" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Как пользоваться")}</p>
            <h2 id="help-article-title" className="text-[15px] font-bold leading-tight text-foreground">{state?.title ?? "…"}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t("Закрыть")} className="grid size-8 flex-none place-items-center rounded-btn text-muted-foreground hover:bg-background-100 hover:text-foreground">
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 md:px-5" data-testid="help-article">
          {error && <p className="text-[13px] text-red-600">{error}</p>}
          {state && <Markdown text={state.body} />}
        </div>
        {app && (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 md:px-5">
            <button
              type="button"
              onClick={() => onOpenSection(app.type)}
              className={cn("inline-flex h-9 items-center gap-1.5 rounded-btn bg-primary px-3.5 text-[13px] font-semibold text-primary-foreground transition hover:opacity-90")}
            >
              {t("Открыть раздел «{name}»", { name: app.label })}
              <ArrowRight className="size-4" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </div>,
    host,
  );
}
